import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { Container } from '@mui/material';
import { emailTemplateService } from '../services/api';

interface EmailTemplateData {
  id?: number;
  client_id?: number;
  assessment_type?: 'depart' | 'retour' | null;
  header_image_path?: string;
  logo_path?: string;
  footer_logo_path?: string;
  header_image_url?: string;
  logo_url?: string;
  footer_logo_url?: string;
  society_name?: string;
  society_address?: string;
  society_siret?: string;
  society_phone?: string;
  society_email?: string;
  free_text?: string;
  is_active?: boolean;
}

const EmailTemplate: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'depart' | 'retour'>('depart');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [template, setTemplate] = useState<EmailTemplateData>({
    is_active: true,
  });

  const [headerImagePreview, setHeaderImagePreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [footerLogoPreview, setFooterLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    loadTemplate();
  }, [activeTab]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await emailTemplateService.getTemplate(activeTab);
      const templateData = response.data.data || {};
      setTemplate({ ...templateData, assessment_type: activeTab });
      
      // Set previews for existing images
      if (templateData.header_image_url) {
        setHeaderImagePreview(templateData.header_image_url);
      } else {
        setHeaderImagePreview(null);
      }
      if (templateData.logo_url) {
        setLogoPreview(templateData.logo_url);
      } else {
        setLogoPreview(null);
      }
      if (templateData.footer_logo_url) {
        setFooterLogoPreview(templateData.footer_logo_url);
      } else {
        setFooterLogoPreview(null);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement du template: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (type: 'header_image' | 'logo' | 'footer_logo', file: File | null) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      if (type === 'header_image') {
        setHeaderImagePreview(result);
      } else if (type === 'logo') {
        setLogoPreview(result);
      } else if (type === 'footer_logo') {
        setFooterLogoPreview(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteImage = async (type: 'header_image' | 'logo' | 'footer_logo') => {
    try {
      await emailTemplateService.deleteImage(type, activeTab);
      if (type === 'header_image') {
        setHeaderImagePreview(null);
        setTemplate({ ...template, header_image_path: undefined, header_image_url: undefined });
      } else if (type === 'logo') {
        setLogoPreview(null);
        setTemplate({ ...template, logo_path: undefined, logo_url: undefined });
      } else if (type === 'footer_logo') {
        setFooterLogoPreview(null);
        setTemplate({ ...template, footer_logo_path: undefined, footer_logo_url: undefined });
      }
      setSuccess('Image supprimée avec succès');
    } catch (err: any) {
      setError('Erreur lors de la suppression: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Add assessment type
      formData.append('assessment_type', activeTab);

      // Add text fields
      if (template.society_name) formData.append('society_name', template.society_name);
      if (template.society_address) formData.append('society_address', template.society_address);
      if (template.society_siret) formData.append('society_siret', template.society_siret);
      if (template.society_phone) formData.append('society_phone', template.society_phone);
      if (template.society_email) formData.append('society_email', template.society_email);
      if (template.free_text) formData.append('free_text', template.free_text);
      formData.append('is_active', template.is_active ? '1' : '0');

      // Add image files (if new files are selected)
      const headerImageInput = document.getElementById('header_image') as HTMLInputElement;
      if (headerImageInput?.files?.[0]) {
        formData.append('header_image', headerImageInput.files[0]);
      }

      const logoInput = document.getElementById('logo') as HTMLInputElement;
      if (logoInput?.files?.[0]) {
        formData.append('logo', logoInput.files[0]);
      }

      const footerLogoInput = document.getElementById('footer_logo') as HTMLInputElement;
      if (footerLogoInput?.files?.[0]) {
        formData.append('footer_logo', footerLogoInput.files[0]);
      }

      const response = await emailTemplateService.saveTemplate(formData);
      setTemplate(response.data.data);
      setSuccess('Template sauvegardé avec succès!');
      
      // Update previews if new images were uploaded
      if (response.data.data.header_image_url) {
        setHeaderImagePreview(response.data.data.header_image_url);
      }
      if (response.data.data.logo_url) {
        setLogoPreview(response.data.data.logo_url);
      }
      if (response.data.data.footer_logo_url) {
        setFooterLogoPreview(response.data.data.footer_logo_url);
      }

      // Clear file inputs
      if (headerImageInput) headerImageInput.value = '';
      if (logoInput) logoInput.value = '';
      if (footerLogoInput) footerLogoInput.value = '';
    } catch (err: any) {
      setError('Erreur lors de la sauvegarde: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuration du Template Email
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Template Type Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Template Départ" 
            value="depart"
            sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
          />
          <Tab 
            label="Template Retour" 
            value="retour"
            sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
          />
        </Tabs>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'depart' 
              ? 'Configurez le template email pour les états des lieux de départ'
              : 'Configurez le template email pour les états des lieux de retour'}
          </Typography>
        </Box>
      </Card>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Header Image Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Image d'en-tête
              </Typography>
              <Box sx={{ mb: 2 }}>
                {headerImagePreview && (
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                    <img
                      src={headerImagePreview}
                      alt="Header preview"
                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteImage('header_image')}
                      sx={{ position: 'absolute', top: 0, right: 0 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  {headerImagePreview ? 'Remplacer l\'image d\'en-tête' : 'Télécharger une image d\'en-tête'}
                  <input
                    type="file"
                    id="header_image"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageChange('header_image', file);
                    }}
                  />
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Logo Sections */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Logo Section */}
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Logo
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {logoPreview && (
                      <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteImage('logo')}
                          sx={{ position: 'absolute', top: 0, right: 0 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {logoPreview ? 'Remplacer le logo' : 'Télécharger un logo'}
                      <input
                        type="file"
                        id="logo"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageChange('logo', file);
                        }}
                      />
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Footer Logo Section */}
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Logo de pied de page
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {footerLogoPreview && (
                      <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                        <img
                          src={footerLogoPreview}
                          alt="Footer logo preview"
                          style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteImage('footer_logo')}
                          sx={{ position: 'absolute', top: 0, right: 0 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {footerLogoPreview ? 'Remplacer le logo de pied de page' : 'Télécharger un logo de pied de page'}
                      <input
                        type="file"
                        id="footer_logo"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageChange('footer_logo', file);
                        }}
                      />
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Society Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations de la société
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Nom de la société"
                  value={template.society_name || ''}
                  onChange={(e) => setTemplate({ ...template, society_name: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Adresse"
                  multiline
                  rows={2}
                  value={template.society_address || ''}
                  onChange={(e) => setTemplate({ ...template, society_address: e.target.value })}
                />
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    label="SIRET"
                    value={template.society_siret || ''}
                    onChange={(e) => setTemplate({ ...template, society_siret: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Téléphone"
                    value={template.society_phone || ''}
                    onChange={(e) => setTemplate({ ...template, society_phone: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={template.society_email || ''}
                  onChange={(e) => setTemplate({ ...template, society_email: e.target.value })}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Free Text Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Texte libre
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Ajoutez du texte personnalisé qui apparaîtra dans l'email..."
                value={template.free_text || ''}
                onChange={(e) => setTemplate({ ...template, free_text: e.target.value })}
              />
            </CardContent>
          </Card>

          {/* Active Toggle */}
          <Card>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={template.is_active ?? true}
                    onChange={(e) => setTemplate({ ...template, is_active: e.target.checked })}
                  />
                }
                label="Template actif"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Si désactivé, le template par défaut sera utilisé pour les emails.
              </Typography>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
              sx={{ backgroundColor: '#ff6b35', '&:hover': { backgroundColor: '#e55a2b' } }}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder le template'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Container>
  );
};

export default EmailTemplate;

